import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import moment from "moment";
import { debounce } from "lodash";
import { safeDivide } from "../../../utils/safeDivide";
import Scrollbars from "react-custom-scrollbars-2";
import { getComparingDates } from "../../../utils/getCompareDates";
import { ArrowRed } from "../../../icons/ArrowRed";
import { ArrowGreen } from "../../../icons/ArrowGreen";
import { useSelector } from "react-redux";
import LargestConceptMovementSummary from "./LargestConceptMovementSummary";
import { useSearchParams } from "react-router-dom";

// Import local CSS modules
import classes from "./BiggestChangeWidget.module.css";
// Import the common module with grid classes
import commonStyles from "./CommonWidget.module.css";

const LargestConceptMovement = (props) => {
  const {
    selectedDateName,
    interval,
    id,
    filters,
    setFilterDocCount,
    selectedPageSize,
    seletctedType,
    selectedDateField,
  } = props;

  const [loading, setLoading] = useState(false);
  const [conceptChanges, setConceptChanges] = useState([]);
  const [comparingDates, setComparingDates] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [filterBaseline, setFilterBaseline] = useState(null);
  const [filterComparison, setFilterComparison] = useState(null);
  const sentiment_share_ai = useSelector((state) => state.auth.flags.sentiment_share_ai);
  const [searchParams] = useSearchParams();
  const isNewApp = searchParams.get("app") === "new";

  // Get font size from Redux store
  const labelFontSize = useSelector((state) => {
    const fontSizes = state.fontManager.fontSizes;
    const key = Object.keys(fontSizes).find((k) => k.trim() === "labelFontSize");
    return fontSizes[key] || 16;
  });

  const [getVolume] = useGetConceptsFiltersMutation();
  const conceptFilter = useMemo(
    () => JSON.stringify(getConceptFilter({ ...props, selectedPageSize })),
    [props, selectedPageSize]
  );

  const getCount = useCallback(
    (concept) => {
      switch (seletctedType) {
        case "exact_match_count":
          return concept.exact_match_count;
        case "conceptual_match_count":
          return concept.match_count - concept.exact_match_count;
        default:
          return concept.match_count;
      }
    },
    [seletctedType]
  );

  const getVolumeData = useCallback(async () => {
    setLoading(true);
    const { data: dataConcepts } = await getVolume({
      id,
      concept_selector: JSON.parse(conceptFilter),
      filter: filters,
      breakdowns: [{ name: selectedDateName, interval }],
    });
    const breakdownData = dataConcepts.breakdowns[0].buckets;
    if (!breakdownData.length) {
      setConceptChanges([]);
      setLoading(false);
      return;
    }
    const lastIndex = breakdownData.length - 1;
    const preLastIndex = breakdownData.length - 2;
    const lastData = breakdownData[lastIndex];
    const preLastData = breakdownData[preLastIndex];

    if (breakdownData.length > 1) {
      setFilterBaseline([{ name: selectedDateName, minimum: preLastData.label, maximum: lastData.label }]);
      setFilterComparison([{ name: selectedDateName, minimum: lastData.label }]);
    } else {
      setFilterBaseline([{ name: selectedDateName, minimum: lastData.label }]);
      setFilterComparison([{ name: selectedDateName, minimum: lastData.label }]);
    }

    const calculatedComparingDates = preLastData
      ? getComparingDates(preLastData, lastData, interval)
      : getComparingDates(lastData, lastData, interval);
    setComparingDates(calculatedComparingDates);

    const updatedConcepts = lastData.match_counts.map((concept) => {
      const preLastConcept = preLastData?.match_counts.find(c => c.name === concept.name);
      const lastCount = safeDivide(getCount(concept), lastData.total_count) * 100;
      const preLastCount = preLastConcept
        ? safeDivide(getCount(preLastConcept), preLastData.total_count) * 100
        : 0;
      const changePercentage = preLastConcept
        ? (lastCount - preLastCount).toFixed(1)
        : lastCount.toFixed(1);
      return { ...concept, changePercentage, lastCount, preLastCount };
    });

    const changes = updatedConcepts.sort(
      (a, b) => Math.abs(b.changePercentage) - Math.abs(a.changePercentage)
    );
    setConceptChanges(changes);
    setSelectedConcept(changes[0] || null);
    setLoading(false);
  }, [getVolume, id, conceptFilter, filters, selectedDateName, interval, getCount]);

  useEffect(() => {
    const debouncedGet = debounce(() => getVolumeData(), 400);
    debouncedGet();
    return () => debouncedGet.cancel();
  }, [getVolumeData]);

  const height = props.widget.layout.h * 39 - 200;
  const EqualCompareDate = comparingDates[0] === comparingDates[1];

  if (loading) {
    return (
      <Spin
        className="spinner-by-center"
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      />
    );
  }

  // --- NEW UI ---
  if (isNewApp) {
    return (
      <div className={classes.widget_container}>
        <div className={classes.left_panel}>
          <div className={classes.visualization}>
            {/* Header */}
            <div className={`${commonStyles.threeColumnGrid} ${commonStyles.gridHeader}`}>
              <div className={commonStyles.gridItem}>Concept</div>
              <div className={commonStyles.gridItem}>Change</div>
              <div className={commonStyles.gridItem}>Movement</div>
            </div>

            <Scrollbars style={{ height }}>
              <div className={commonStyles.gridContainer}>
                {conceptChanges.map((concept, index) => (
                  <div key={concept.name + index} className={`${commonStyles.threeColumnGrid} ${commonStyles.gridRow}`}>
                    <div
                      className={commonStyles.gridItem}
                      style={{ cursor: "pointer", textTransform: "capitalize" }}
                      onClick={() => setSelectedConcept(concept)}
                    >
                      {concept.name}
                    </div>
                    <div className={commonStyles.gridItem}>
                      {!EqualCompareDate &&
                        (concept.changePercentage >= 0 ? <ArrowGreen /> : <ArrowRed />)}
                      <span style={{ fontSize: labelFontSize, marginLeft: 4 }}>
                        {EqualCompareDate ? "0" : Math.abs(concept.changePercentage).toFixed(1)}%
                      </span>
                    </div>
                    <div className={commonStyles.gridItem}>
                      <div className={commonStyles.barContainer}>
                        <div
                          className={commonStyles.bar}
                          style={{ width: `${concept.lastCount}%`, background: "#005D5D" }}
                          title={`${concept.lastCount.toFixed(1)}%`}
                        />
                        <span className={commonStyles.barValue}>
                          {concept.lastCount.toFixed(2)}%
                        </span>
                      </div>
                      {!EqualCompareDate && (
                        <div className={commonStyles.barContainer}>
                          <div
                            className={commonStyles.bar}
                            style={{ width: `${concept.preLastCount}%`, background: "#3DDBD9" }}
                            title={`${concept.preLastCount.toFixed(2)}%`}
                          />
                          <span className={commonStyles.barValue}>
                            {concept.preLastCount.toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Scrollbars>
          </div>

          {/* Date Legends */}
          <div className={classes.legends}>
            {comparingDates?.length > 0 && (
              <div className={classes.date_indicators}>
                <div className={classes.date_indicator}>
                  <div
                    className={classes.bar}
                    style={{
                      height: "16px",
                      width: `${props.widget.layout.w * 2}px`,
                      background: "#005d5d",
                    }}
                  />
                  <div>{comparingDates[0]}</div>
                </div>
                {!EqualCompareDate && (
                  <div className={classes.date_indicator}>
                    <div
                      className={classes.bar}
                      style={{
                        height: "16px",
                        width: `${props.widget.layout.w * 2}px`,
                        background: "#3ddbd9",
                      }}
                    />
                    <div>{comparingDates[1]}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={classes.right_panel}>
          <div className={classes.summary}>
            {sentiment_share_ai && selectedConcept && (
              <LargestConceptMovementSummary
                comparingDates={comparingDates}
                id={id}
                selectedConcept={selectedConcept}
                filterBaseline={filterBaseline}
                filterComparison={filterComparison}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- OLD UI ---
  return (
    <div className={classes.container}>
      <div className={classes.change_type_container}>
        <div className={classes.change_type}>
          <div className={`${commonStyles.threeColumnGrid} ${commonStyles.gridHeader}`}>
            <div className={commonStyles.gridItem}>Concept</div>
            <div className={commonStyles.gridItem}>Change</div>
            <div className={commonStyles.gridItem}>Movement</div>
          </div>
          <Scrollbars style={{ height }}>
            <div className={commonStyles.gridContainer}>
              {conceptChanges.map((concept, index) => (
                <div key={concept.name + index} className={`${commonStyles.threeColumnGrid} ${commonStyles.gridRow}`}>
                  <div
                    className={commonStyles.gridItem}
                    style={{ cursor: "pointer", textTransform: "capitalize" }}
                    onClick={() => setSelectedConcept(concept)}
                  >
                    {concept.name}
                  </div>
                  <div className={commonStyles.gridItem}>
                    {!EqualCompareDate &&
                      (concept.changePercentage >= 0 ? <ArrowGreen /> : <ArrowRed />)}
                    <span style={{ fontSize: labelFontSize, marginLeft: 4 }}>
                      {EqualCompareDate ? "0" : Math.abs(concept.changePercentage).toFixed(1)}%
                    </span>
                  </div>
                  <div className={commonStyles.gridItem}>
                    <div className={commonStyles.barContainer}>
                      <div
                        className={commonStyles.bar}
                        style={{ width: `${concept.lastCount}%`, background: "#005D5D" }}
                        title={`${concept.lastCount.toFixed(1)}%`}
                      />
                      <span className={commonStyles.barValue}>
                        {concept.lastCount.toFixed(2)}%
                      </span>
                    </div>
                    {!EqualCompareDate && (
                      <div className={commonStyles.barContainer}>
                        <div
                          className={commonStyles.bar}
                          style={{ width: `${concept.preLastCount}%`, background: "#3DDBD9" }}
                          title={`${concept.preLastCount.toFixed(2)}%`}
                        />
                        <span className={commonStyles.barValue}>
                          {concept.preLastCount.toFixed(2)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {sentiment_share_ai && selectedConcept && (
              <LargestConceptMovementSummary
                comparingDates={comparingDates}
                id={id}
                selectedConcept={selectedConcept}
                filterBaseline={filterBaseline}
                filterComparison={filterComparison}
              />
            )}
          </Scrollbars>
        </div>
        {comparingDates?.length > 0 && (
          <div className={classes.date_indicators}>
            <div className={classes.date_indicator}>
              <div
                className={classes.bar}
                style={{
                  height: "16px",
                  width: `${props.widget.layout.w * 2}px`,
                  background: "#005d5d",
                }}
              />
              <div>{comparingDates[0]}</div>
            </div>
            {!EqualCompareDate && (
              <div className={classes.date_indicator}>
                <div
                  className={classes.bar}
                  style={{
                    height: "16px",
                    width: `${props.widget.layout.w * 2}px`,
                    background: "#3ddbd9",
                  }}
                />
                <div>{comparingDates[1]}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LargestConceptMovement;
